import { Component, input } from '@angular/core';

@Component({
  selector: 'app-button',
  standalone: true,
  template: `
    <button 
      class="btn" 
      [class]="class()" 
      [type]="type()" 
      [disabled]="disabled() || loading()" 
      [attr.title]="title()" 
      [attr.form]="form()" 
      [attr.aria-label]="ariaLabel() || label()"
      aria-live="polite">
      @if (loading()) {
        <div class="loader-container">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
      } @else if (icon()) {
        <i
          class="bi"
          [class]="iconCssClass()"
          aria-hidden="true"
        ></i>
      }
      @if (label()) {
        <span class="btn-label">{{ loading() ? (loadingLabel() || label()) : label() }}</span>
      }
    </button>
  `,
  styles: `
    @use 'sass:map';
    @use 'sass:color';
    @use 'variables' as *;
    @use 'mixins' as *;

    :host{
      @include flex(row, center, center, nowrap, $space-2);
    }

    .btn {
      width: 100%;
      @include flex(row, center, center, nowrap, $space-2);
      min-height: 44px;
      padding: $space-2 $space-4;
      border-radius: $radius-md;
      font-size: $font-size-sm;
      font-weight: $font-weight-semibold;
      cursor: pointer;
      border: 1px solid transparent;
      transition: $transition-fast;
      user-select: none;
      @include transition(all);

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
      }

      @include respond-to('md') {
        padding: $space-2 $space-6;
        font-size: $font-size-md;
      }

      // Solid Variants
      &.primary { background: $color-primary; color: white; &:hover { background: $color-primary-dark; } }
      &.secondary { background: map.get($colors, 'secondary', 'base'); color: white; &:hover { background: map.get($colors, 'secondary', 'dark'); } }
      &.success { background: map.get($colors, 'success', 'base'); color: white; &:hover { background: map.get($colors, 'success', 'dark'); } }
      &.info { background: map.get($colors, 'info', 'base'); color: white; &:hover { background: map.get($colors, 'info', 'dark'); } }
      &.error, &.danger { background: map.get($colors, 'error', 'base'); color: white; &:hover { background: map.get($colors, 'error', 'dark'); } }
      &.warning { background: map.get($colors, 'warning', 'base'); color: white; &:hover { background: map.get($colors, 'warning', 'dark'); } }
      &.dark { background: $color-verde-escuro; color: white; &:hover { background: color.adjust($color-verde-escuro, $lightness: -5%); } }
      &.light { background: $color-bg-light; color: $color-text-dark; border-color: $border-light; &:hover { background: color.adjust($color-bg-light, $lightness: -3%); } }

      // Outline Variants
      &.outline {
        background: transparent;
        border-color: $color-primary;
        color: $color-primary;
        &:hover { background: rgba($color-primary, 0.05); }

        &.success { border-color: map.get($colors, 'success', 'base'); color: map.get($colors, 'success', 'base'); &:hover { background: rgba(map.get($colors, 'success', 'base'), 0.05); } }
        &.danger { border-color: map.get($colors, 'error', 'base'); color: map.get($colors, 'error', 'base'); &:hover { background: rgba(map.get($colors, 'error', 'base'), 0.05); } }
        &.warning { border-color: map.get($colors, 'warning', 'base'); color: map.get($colors, 'warning', 'base'); &:hover { background: rgba(map.get($colors, 'warning', 'base'), 0.05); } }
        &.info { border-color: map.get($colors, 'info', 'base'); color: map.get($colors, 'info', 'base'); &:hover { background: rgba(map.get($colors, 'info', 'base'), 0.05); } }
      }

      &.ghost {
        background: transparent;
        color: $color-text-medium;
        &:hover { background: $color-bg-light; color: $color-primary; }
        
        &.danger { color: $color-error; &:hover { background: rgba($color-error, 0.05); color: $color-error; } }
        &.success { color: $color-success; &:hover { background: rgba($color-success, 0.05); color: $color-success; } }
        &.info { color: $color-info; &:hover { background: rgba($color-info, 0.05); color: $color-info; } }
        &.warning { color: $color-warning; &:hover { background: rgba($color-warning, 0.05); color: $color-warning; } }
        &.dark { color: $color-verde-escuro; &:hover { background: rgba($color-verde-escuro, 0.05); color: $color-verde-escuro; } }
        &.light { color: $color-bg-light; &:hover { background: rgba($color-bg-light, 0.05); color: $color-bg-light; } }
        
      }

      &.dashed {
        @include button-reset;
        @include flex(row, center, center, $gap: $space-2);
        padding: $space-3;
        border: 1.5px dashed $border-color;
        border-radius: $radius-lg;
        font-size: $font-size-xs;
        font-weight: 600;
        color: $color-text-light;
        @include transition(all);
        background: transparent;

        &:hover {
          border-color: $color-primary;
          color: $color-primary;
          background: $color-primary-light;
        }
      }

      &.link {
        background: transparent;
        color: $color-primary;
        min-height: auto;
        padding: 0;
        width: auto;
        &:hover { text-decoration: underline; background: transparent; }
      }

      // Sizes
      &.sm {
        min-height: 32px;
        padding: $space-1 $space-3;
        font-size: $font-size-xs;
      }

      &.lg {
        min-height: 52px;
        padding: $space-3 $space-8;
        font-size: $font-size-lg;
      }

      .loader-container {
        display: flex;
        gap: 4px;

        .dot {
          width: 8px;
          height: 8px;
          background-color: currentColor;
          border-radius: 50%;
          animation: dot-pulse 1.4s infinite ease-in-out both;

          &:nth-child(1) { animation-delay: -0.32s; }
          &:nth-child(2) { animation-delay: -0.16s; }
        }
      }
    }

    .dashed {
        @include button-reset;
        @include flex(row, center, center, $gap: $space-2);
        padding: $space-3;
        border: 1.5px dashed $border-color;
        border-radius: $radius-lg;
        font-size: $font-size-xs;
        font-weight: 600;
        color: $color-text-light;
        @include transition(all);
        background: transparent;
        width: 100%;

        &:hover {
          border-color: $color-primary;
          color: $color-primary;
          background: $color-primary-light;
        }
      }

    @keyframes dot-pulse {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1.0); }
    }
  `,
})
export class ButtonComponent {
  icon = input<string>('');
  label = input<string>('');
  ariaLabel = input<string>(''); // Novo input para acessibilidade

  class = input<string>('');
  type = input<string>('button');
  loading = input<boolean>(false);
  disabled = input<boolean>(false);
  title = input<string>('');
  form = input<string>('');
  loadingLabel = input<string>('');
  iconClass = input<string>('');

  iconCssClass(): string {
    return [this.icon(), this.iconClass()].filter(Boolean).join(' ');
  }
}
